import { Component, ElementRef, EventEmitter, HostListener, Inject, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DateRange, MatDateRangePicker } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import moment from "moment";
import { TokenEncryptionService } from 'src/app/Services/token-encryption.service';
import { SharedService } from 'src/app/Services/sharedService';
import { TimesheetDatePreset, TimesheetPresetDateRangeService } from 'src/app/Services/timesheet-preset-date-range.service';
import { EntitlementsService } from 'src/app/Services/entitlements.service';
import { UI_ADVANCED_FILTER_DEFAULTS, UI_DEEP_WORK_FILTER_DEFAULTS, UiDeepWorkFilterDefaults } from 'src/app/entitlements/report-advanced-filters.util';

@Component({
  selector: 'app-filter-dropdown',
  templateUrl: './filter-dropdown.component.html',
  styleUrls: ['./filter-dropdown.component.css'],
  standalone: false
})
export class FilterDropdownComponent {
  @Input('filterData') filterData: any;
  usersList: any[] = [];
  teamsList: any[] = [];
  loading: boolean = false;
  type: 'all' | 'app' | 'website' = 'all';
  selectedDate: { start: Date | null, end: Date | null } = { start: null, end: null };
  selectedShiftInput: any = null;
  selectedEmployeesInput: any[] = [];
  selectedTeamInput: any = null;
  shifts: any[] = [];
  /** Max span for one report/PT query — follows plan log retention (not DM 31-day export limit). */
  private isAdjustingDateRange = false;
  @ViewChild('picker') picker!: MatDateRangePicker<Date>;
  @ViewChild('dateRangeDropdown') dateRangeDropdown!: ElementRef;
  @ViewChild('dateRangeButton') dateRangeButton!: ElementRef;
  @Output() filterChanged = new EventEmitter<any>();

  // Tabs and date
  tab: 'all' | 'app' | 'website' = 'all';
  dateRange: string = 'last14daysExcludeToday';
  minDate: any = new Date().toISOString();
  maxDate: any = new Date().toISOString();
  // Dropdown states
  isOpenDateDropdown = false;
  employeeTeamTab: 'employee' | 'team' = 'employee';

  // Employee / Team selections (temporary inside dropdown)
  employeeCheckboxMap: { [key: string]: boolean } = {};
  teamCheckboxMap: { [key: string]: boolean } = {};
  // Shift selection
  selectedShift: any = null;
  isShiftDropdownOpen = false;
  presetFilters = [
    { value: "today", label: 'Today' },
    { value: "last7days", label: 'Last 7 Days' },
    { value: "last14daysExcludeToday", label: 'Last 14 Days (excl. today)' },
    { value: "thisMonth", label: 'This Month' },
    { value: "previousMonth", label: 'Previous Month' },
    { value: "last90days", label: 'Last 3 Months' },
  ];
  isOpenAsDialog = false;
  filteredEmployeeList: any[] = [];
  filteredTeamList: any[] = [];
  selectedDateRange = new FormGroup<{ start: FormControl<Date | null>, end: FormControl<Date | null> }>({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });
  isApplyCustomRange: boolean = false;
  isAdvancedFilterOpen: boolean = false;
  selectedQuickPreset: string | null = null;
  quickPresets = [
    { id: 'standard', label: 'Standard Work Hours', icon: 'schedule' },
    { id: 'overtime', label: 'Overtime Analysis', icon: 'trending_up' },
    { id: 'weekend', label: 'Weekend Productivity', icon: 'calendar_today' },
    { id: 'night', label: 'Night Shift Insights', icon: 'dark_mode' },
  ];

  // Shift & Time Filter States
  selectedDaySelection: 'all' | 'weekdays' | 'weekends' | 'custom' = 'all';
  selectedTimeWindow: 'fullDay' | 'shiftHours' | 'custom' = 'fullDay';
  selectedInterpretationMode: 'flexible' | 'strict' = 'flexible';
  selectedBufferSetting: 'default' | 'custom' = 'default';
  beforeBufferTime: number = 15;
  afterBufferTime: number = 15;
  postShiftAnalysisWindowHours: number = 0;
  postShiftAnalysisWindowMins: number = 0;

  weekDays: string[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  selectedCustomDays: { [key: string]: boolean } = {
    'Mon': true, 'Tue': true, 'Wed': true, 'Thu': true, 'Fri': true
  };

  customStartTime: string = '09:00';
  customEndTime: string = '18:00';
  deepWorkMinSessionMinutes: number = 90;
  deepWorkMinProductivityPct: number = 70;
  deepWorkMaxDistractionPct: number = 20;
  /** Productivity Trends: show deep work rules in the main filter drawer (not inside Advanced). */
  showDeepWorkSettings = false;
  readonly deepWorkDurationOptions: Array<{ value: number; label: string; hint: string }> = [
    { value: 60, label: '60 min', hint: '1.0 h' },
    { value: 90, label: '90 min', hint: '1.5 h' },
    { value: 120, label: '120 min', hint: '2.0 h' },
    { value: 150, label: '150 min', hint: '2.5 h' },
  ];

  /** Values emitted as filterPayload.compareWith (normalized to API snake_case in productivity trends). */
  selectedCompareWith: 'none' | 'previousPeriod' | 'previousWeek' | 'previousMonth' = 'previousPeriod';
  readonly comparePeriodOptions: Array<{ value: 'none' | 'previousPeriod' | 'previousWeek' | 'previousMonth'; label: string }> = [
    { value: 'none', label: 'None' },
    { value: 'previousPeriod', label: 'Previous period' },
    { value: 'previousWeek', label: 'Previous week' },
    { value: 'previousMonth', label: 'Previous month' },
  ];

  get isNightShift(): boolean {
    const start = this.selectedTimeWindow === 'custom' ? this.customStartTime : (this.selectedShift?.start_time || this.selectedShift?.shiftStartTime);
    const end = this.selectedTimeWindow === 'custom' ? this.customEndTime : (this.selectedShift?.end_time || this.selectedShift?.shiftEndTime);
    if (!start || !end) return false;
    return end < start;
  }

  constructor(
    private el: ElementRef,
    private tokenEncryptionService: TokenEncryptionService,
    private _sharedService: SharedService,
    private _timesheetPresetDateRange: TimesheetPresetDateRangeService,
    private entitlementsService: EntitlementsService
  ) { }

  canUseAdvancedFilters(): boolean {
    return this.entitlementsService.hasReportsAdvancedFilters();
  }

  canUseDeepWorkFilters(): boolean {
    return this.entitlementsService.canConfigureDeepWorkFilters();
  }

  /** Inclusive day span allowed in the date picker (aligned with users/me logs retention). */
  private getMaxRangeDays(): number {
    return Math.max(1, this.entitlementsService.getLogsRetentionDays());
  }

  private deepWorkFieldsForPayload(): UiDeepWorkFilterDefaults {
    return this.canUseDeepWorkFilters()
      ? {
        deepWorkMinSessionMinutes: this.deepWorkMinSessionMinutes,
        deepWorkMinProductivityPct: this.deepWorkMinProductivityPct,
        deepWorkMaxDistractionPct: this.deepWorkMaxDistractionPct,
      }
      : { ...UI_DEEP_WORK_FILTER_DEFAULTS };
  }

  private normalizeIncomingCompareWith(raw: unknown): 'none' | 'previousPeriod' | 'previousWeek' | 'previousMonth' {
    if (raw === undefined || raw === null) return 'previousPeriod';
    const s = String(raw).trim();
    if (s === '') return 'previousPeriod';
    if (s === 'previousPeriod' || s === 'previous_period') return 'previousPeriod';
    if (s === 'previousWeek' || s === 'previous_week') return 'previousWeek';
    if (s === 'previousMonth' || s === 'previous_month') return 'previousMonth';
    const lower = s.toLowerCase();
    if (lower === 'none') return 'none';
    return 'previousPeriod';
  }

  setCompareWith(mode: 'none' | 'previousPeriod' | 'previousWeek' | 'previousMonth'): void {
    this.selectedCompareWith = mode;
  }

  ngOnInit(): void {
    this.entitlementsService.syncFromSession();
    this.setTimeRangeForDateInputs();
  }

  setTimeRangeForDateInputs() {
    const user = this.tokenEncryptionService.getEncryptedItem<any>('user');
    const companyId = this.tokenEncryptionService.getEncryptedItem<any>('logedInUser')?.company_id ?? user?.company_id;
    const dateToUse = user?.meta?.today_date || new Date();
    this._sharedService.getValidationDateRangeByCompany(dateToUse, companyId, {
      lookbackDays: this.entitlementsService.getLogsRetentionDays(),
    }).subscribe((range) => {
      this.maxDate = range.maxDate;
      this.minDate = range.minDate;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {

    if (changes['filterData'] && this.filterData) {
      this.usersList = this.filterData.usersList;
      this.teamsList = this.filterData.teamsList;
      this.loading = this.filterData.loading;
      this.type = this.filterData.type;
      this.selectedDate = this.filterData.dateRange;
      this.selectedShiftInput = this.filterData.selectedShift;
      this.selectedEmployeesInput = this.filterData.selectedEmployees;
      this.selectedTeamInput = this.filterData.selectedTeams;
      this.shifts = this.filterData.shifts;

      this.setDataAccordingChanges();
    }

    if (changes['filterData'] && this.usersList) {
      this.filteredEmployeeList = [...this.usersList];
    }

    if (changes['filterData'] && this.teamsList) {
      this.filteredTeamList = [...this.teamsList];
    }

    if (changes['filterData'] && this.shifts && this.shifts.length > 0) {
      if (this.selectedShiftInput !== undefined && this.selectedShiftInput !== null) {
        this.selectedShift = this.shifts.find(s => s.id == this.selectedShiftInput) || null;
      } else {
        if (this.selectedShift === undefined) {
          this.selectedShift = null;
        }
      }
    }
  }

  setDataAccordingChanges() {
    this.tab = this.type || 'all';
    if (this.selectedDate && this.selectedDate.start && this.selectedDate.end) {
      this.selectedDateRange.patchValue({
        start: this.selectedDate.start,
        end: this.selectedDate.end
      });
    } else {
      this.setDateAccordingOption(this.dateRange);
    }
    this.enforceMaxRangeFromCurrentSelection();
    this.selectedDateRange.valueChanges.subscribe(() => this.enforceMaxRangeFromCurrentSelection());
    // Initialize selected shift from input or default to null (None)
    if (this.selectedShiftInput !== undefined && this.selectedShiftInput !== null) {
      this.selectedShift = this.shifts.find(s => s.id == this.selectedShiftInput) || null;
      // Only apply shift defaults if user hasn't already customized the advanced filters
      const hasCustomAdvanced = this.filterData &&
        (this.filterData.daySelection !== 'all'
          || this.filterData.timeWindow !== 'fullDay'
          || this.filterData.interpretationMode !== 'flexible'
          || this.filterData.bufferSetting !== 'default');
      if (this.selectedShift && !hasCustomAdvanced && this.canUseAdvancedFilters()) {
        this.applyShiftDefaults(this.selectedShift);
      }
    } else {
      this.selectedShift = null;
    }

    this.employeeCheckboxMap = {};
    if (this.selectedEmployeesInput && this.selectedEmployeesInput.length > 0) {
      this.selectedEmployeesInput.forEach((emp: any) => {
        this.employeeCheckboxMap[emp] = true;
      });
    }

    this.teamCheckboxMap = {};
    if (this.selectedTeamInput && this.selectedTeamInput.length > 0) {
      this.selectedTeamInput.forEach((team: any) => {
        this.teamCheckboxMap[team] = true;
      });
    }

    if (this.selectedTeamInput && this.selectedTeamInput.length > 0 && (!this.selectedEmployeesInput || this.selectedEmployeesInput.length === 0)) {
      this.employeeTeamTab = 'team';
    } else {
      this.employeeTeamTab = 'employee';
    }

    if (!this.selectedDate || !this.selectedDate.start || !this.selectedDate.end) {
      this.setDateAccordingOption(this.dateRange);
    }

    // ── Restore advanced filter state from filterData ──
    if (this.filterData) {
      this.selectedCompareWith = this.normalizeIncomingCompareWith(this.filterData.compareWith);
      this.selectedDaySelection = this.filterData.daySelection ?? 'all';
      this.selectedTimeWindow = this.filterData.timeWindow ?? 'fullDay';
      this.selectedInterpretationMode = this.filterData.interpretationMode ?? 'flexible';
      this.selectedBufferSetting = this.filterData.bufferSetting ?? 'default';

      if (this.filterData.customDays) {
        this.selectedCustomDays = { ...this.filterData.customDays };
      }

      if (this.filterData.startTime) {
        this.customStartTime = this.filterData.startTime;
      }
      if (this.filterData.endTime) {
        this.customEndTime = this.filterData.endTime;
      }

      this.beforeBufferTime = this.filterData.beforeBufferTime ?? 15;
      this.afterBufferTime = this.filterData.afterBufferTime ?? 15;

      if (this.filterData.postShiftAnalysisWindowMinutes) {
        const mins = this.filterData.postShiftAnalysisWindowMinutes;
        this.postShiftAnalysisWindowHours = Math.floor(mins / 60);
        this.postShiftAnalysisWindowMins = mins % 60;
      } else {
        this.postShiftAnalysisWindowHours = 0;
        this.postShiftAnalysisWindowMins = 0;
      }
      this.deepWorkMinSessionMinutes = Number(this.filterData.deepWorkMinSessionMinutes ?? 90);
      this.deepWorkMinProductivityPct = Number(this.filterData.deepWorkMinProductivityPct ?? 70);
      this.deepWorkMaxDistractionPct = Number(this.filterData.deepWorkMaxDistractionPct ?? 20);
      this.showDeepWorkSettings = !!this.filterData.showDeepWorkSettings;
      if (this.showDeepWorkSettings && !this.canUseDeepWorkFilters()) {
        this.deepWorkMinSessionMinutes = UI_DEEP_WORK_FILTER_DEFAULTS.deepWorkMinSessionMinutes;
        this.deepWorkMinProductivityPct = UI_DEEP_WORK_FILTER_DEFAULTS.deepWorkMinProductivityPct;
        this.deepWorkMaxDistractionPct = UI_DEEP_WORK_FILTER_DEFAULTS.deepWorkMaxDistractionPct;
      }

      // ── Auto-open advanced filter panel if any advanced setting is active ──
      const hasActiveAdvancedFilter =
        this.canUseAdvancedFilters()
        && (
          this.filterData.daySelection !== 'all'
          || this.filterData.timeWindow !== 'fullDay'
          || this.filterData.interpretationMode !== 'flexible'
          || this.filterData.bufferSetting !== 'default'
        );
      if (hasActiveAdvancedFilter) {
        this.isAdvancedFilterOpen = true;
      } else if (!this.canUseAdvancedFilters()) {
        this.isAdvancedFilterOpen = false;
      }
    }
  }

  closeDialog() {
    this._sharedService.componentDrawerSubject.next({
      componentName: "reports-filter",
      componentData: null,
      componentAction: "close",
      componentWidth: 0
    });
  }

  setTab(tab: 'all' | 'app' | 'website') {
    this.tab = tab;
  }

  setDateRange(range: any) {
    this.dateRange = range.value;
    this.setDateAccordingOption(range.value)
  }

  setCustomDateRange() {
    this.picker.open();
  }

  toggleEmployeeCheckbox(employeeId: string | number) {
    // flip the checkbox state
    this.employeeCheckboxMap[employeeId] = !this.employeeCheckboxMap[employeeId];
    this.teamCheckboxMap = {}; // Enforce mutual exclusivity
  }

  toggleTeamCheckbox(teamId: string | number) {
    const isSelected = this.teamCheckboxMap[teamId];
    this.teamCheckboxMap = {};
    this.employeeCheckboxMap = {}; // Enforce mutual exclusivity
    if (!isSelected) {
      this.teamCheckboxMap[teamId] = true;
    }
  }

  /**
   * Called when a user row or checkbox is clicked. Updates the map and
   * keeps the temporary employee array consistent so selections persist
   * until the user applies or clears the filter.
   */
  toggleEmployeeSelection(user: any) {
    if (!user || (user.id === undefined || user.id === null)) return;
    this.toggleEmployeeCheckbox(user.id);
  }

  selectMode(mode: any) {
    this.employeeTeamTab = mode;
    this.filteredEmployeeList = [...this.usersList];
    this.filteredTeamList = [...this.teamsList];
  }

  /**
   * Clear all selected employees and teams, both in the checkbox maps and the temporary arrays.
   */
  clearAllEmployeeTeamSelections() {
    this.employeeCheckboxMap = {};
    this.teamCheckboxMap = {};
  }

  searchTimelineData(searchTerm: string) {
    if (this.employeeTeamTab == "employee") {
      if (!searchTerm) {
        this.filteredEmployeeList = [...this.usersList];
      } else {
        const lowerSearch = searchTerm.toLowerCase();
        this.filteredEmployeeList = this.usersList.filter(user =>
          user?.name?.toLowerCase().includes(lowerSearch)
        );
      }
    } else {
      if (!searchTerm) {
        this.filteredTeamList = [...this.teamsList];
      } else {
        const lowerSearch = searchTerm.toLowerCase();
        this.filteredTeamList = this.teamsList.filter(team =>
          team?.title?.rendered?.toLowerCase().includes(lowerSearch)
        );
      }
    }
  }

  // Mirror behavior for teams so that checkbox map and tempSelectedTeams stay in sync
  toggleTeamSelection(team: any) {
    if (!team || (team.id === undefined || team.id === null)) return;
    this.toggleTeamCheckbox(team.id);
  }

  clearEmployeeTeamSelection() {
    this.employeeCheckboxMap = {};
    this.teamCheckboxMap = {};
  }

  toggleAdvancedFilter() {
    this.isAdvancedFilterOpen = !this.isAdvancedFilterOpen;
  }

  applyQuickPreset(presetId: string) {
    // If toggling the same preset off, deselect it
    if (this.selectedQuickPreset === presetId) {
      this.selectedQuickPreset = null;
      return;
    }
    this.selectedQuickPreset = presetId;

    // Quick presets only configure the advanced filter fields.
    // They do NOT modify the date range or the shift selection.
    switch (presetId) {
      case 'standard':
        // Standard Work Hours:
        //  - Day selection: Weekdays
        //  - Time window: Custom (9:00 AM – 6:00 PM)
        //  - Interpretation: Flexible
        this.selectedDaySelection = 'weekdays';
        this.selectedCustomDays = { 'Mon': true, 'Tue': true, 'Wed': true, 'Thu': true, 'Fri': true, 'Sat': false, 'Sun': false };
        this.selectedTimeWindow = 'custom';
        this.customStartTime = '09:00';
        this.customEndTime = '18:00';
        this.selectedInterpretationMode = 'flexible';
        this.selectedBufferSetting = 'default';
        break;

      case 'overtime':
        // Overtime Analysis:
        //  - Day selection: All
        //  - Time window: Full Day (default)
        //  - Interpretation: Flexible
        this.selectedDaySelection = 'all';
        this.selectedTimeWindow = this.selectedShift ? 'shiftHours' : 'fullDay';
        this.selectedInterpretationMode = 'flexible';
        this.selectedBufferSetting = 'default';
        break;

      case 'weekend':
        // Weekend Productivity:
        //  - Day selection: Weekends
        //  - Time window: Full Day
        //  - Interpretation: Flexible
        this.selectedDaySelection = 'weekends';
        this.selectedCustomDays = { 'Mon': false, 'Tue': false, 'Wed': false, 'Thu': false, 'Fri': false, 'Sat': true, 'Sun': true };
        this.selectedTimeWindow = this.selectedShift ? 'shiftHours' : 'fullDay';
        this.selectedInterpretationMode = 'flexible';
        this.selectedBufferSetting = 'default';
        break;

      case 'night':
        // Night Shift Insights:
        //  - Day selection: All
        //  - Time window: Custom (8:00 PM – 6:00 AM)
        //  - Interpretation: Strict
        //  - Buffer: Custom 15 min start + 15 min end
        this.selectedDaySelection = 'all';
        this.selectedTimeWindow = 'custom';
        this.customStartTime = '20:00';
        this.customEndTime = '06:00';
        this.selectedInterpretationMode = 'strict';
        this.selectedBufferSetting = 'custom';
        this.beforeBufferTime = 15;
        this.afterBufferTime = 15;
        break;
    }
  }

  setDaySelection(mode: any) { this.selectedDaySelection = mode; }
  setTimeWindow(mode: any) { this.selectedTimeWindow = mode; }
  setInterpretationMode(mode: any) { this.selectedInterpretationMode = mode; }
  setBufferSetting(mode: any) { this.selectedBufferSetting = mode; }

  setDeepWorkDuration(minutes: number): void {
    this.deepWorkMinSessionMinutes = minutes;
  }

  deepWorkDurationLabel(): string {
    const match = this.deepWorkDurationOptions.find((o) => o.value === this.deepWorkMinSessionMinutes);
    return match ? `${match.label} (${match.hint})` : `${this.deepWorkMinSessionMinutes} min`;
  }

  toggleBufferSetting() {
    this.selectedBufferSetting = this.selectedBufferSetting === 'custom' ? 'default' : 'custom';
  }

  toggleCustomDay(day: string) {
    this.selectedCustomDays[day] = !this.selectedCustomDays[day];

    const selectedDaysCount = Object.values(this.selectedCustomDays).filter(isSelected => isSelected).length;
    if (selectedDaysCount < 1) {
      this.weekDays.forEach(d => {
        this.selectedCustomDays[d] = true;
      });
    }
  }

  toggleShiftDropdown() {
    this.isShiftDropdownOpen = !this.isShiftDropdownOpen;
  }

  selectShift(shift: any) {
    this.selectedShift = shift;
    this.isShiftDropdownOpen = false;
    if (this.canUseAdvancedFilters()) {
      this.applyShiftDefaults(shift);
    }
  }

  applyShiftDefaults(shift: any) {
    if (shift) {
      // 1. Day Selection
      const days = shift.working_days || [];
      const isMonFri = days.length === 5 && [1, 2, 3, 4, 5].every(d => days.includes(d));
      const isWeekend = days.length === 2 && [6, 7].every(d => days.includes(d));
      const isAllDays = days.length === 7;

      if (isAllDays) {
        this.selectedDaySelection = 'all';
      } else if (isMonFri) {
        this.selectedDaySelection = 'weekdays';
      } else if (isWeekend) {
        this.selectedDaySelection = 'weekends';
      } else {
        this.selectedDaySelection = 'custom';
        const dayMap: { [key: number]: string } = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' };
        this.weekDays.forEach(d => this.selectedCustomDays[d] = false);
        days.forEach((d: number) => {
          if (dayMap[d]) this.selectedCustomDays[dayMap[d]] = true;
        });
      }

      // 2. Time Window
      this.selectedTimeWindow = 'shiftHours';

      // Sync custom time inputs with shift hours (formatted to HH:mm)
      if (shift.start_time) {
        this.customStartTime = shift.start_time.substring(0, 5);
      }
      if (shift.end_time) {
        this.customEndTime = shift.end_time.substring(0, 5);
      }

      // 3. Work Interpretation Mode (Only strict if explicitly set in shift)
      if (shift.strict_window) {
        this.selectedInterpretationMode = 'strict';
      } else {
        this.selectedInterpretationMode = 'flexible';
      }

      // 4. Buffer Settings
      if (shift.buffer_start_minutes > 0 || shift.buffer_end_minutes > 0) {
        this.selectedBufferSetting = 'custom';
        this.beforeBufferTime = shift.buffer_start_minutes;
        this.afterBufferTime = shift.buffer_end_minutes;
      } else {
        this.selectedBufferSetting = 'default';
        this.beforeBufferTime = 15;
        this.afterBufferTime = 15;
      }

      // 5. Clear quick preset as we're applying shift defaults
      this.selectedQuickPreset = null;
    } else {
      // Reset to defaults
      this.selectedDaySelection = 'all';
      this.selectedTimeWindow = 'fullDay';
      this.selectedInterpretationMode = 'flexible';
      this.selectedBufferSetting = 'default';
      this.beforeBufferTime = 15;
      this.afterBufferTime = 15;
      this.selectedQuickPreset = null;
    }
  }

  applyFilters() {
    this.isOpenDateDropdown = false;

    const advancedFields = this.canUseAdvancedFilters()
      ? {
        daySelection: this.selectedDaySelection,
        timeWindow: this.selectedTimeWindow,
        interpretationMode: this.selectedInterpretationMode,
        bufferSetting: this.selectedBufferSetting,
        customDays: this.selectedDaySelection === 'custom' ? this.selectedCustomDays : null,
        startTime: this.selectedTimeWindow === 'custom' ? this.customStartTime : null,
        endTime: this.selectedTimeWindow === 'custom' ? this.customEndTime : null,
        beforeBufferTime: this.selectedBufferSetting === 'custom' ? this.beforeBufferTime : 0,
        afterBufferTime: this.selectedBufferSetting === 'custom' ? this.afterBufferTime : 0,
        postShiftAnalysisWindowMinutes: (this.postShiftAnalysisWindowHours * 60) + (this.postShiftAnalysisWindowMins || 0),
      }
      : { ...UI_ADVANCED_FILTER_DEFAULTS };

    const payload = {
      isReset: false,
      type: this.tab,
      dateRange: {
        start: this.selectedDateRange.value.start,
        end: this.selectedDateRange.value.end
      },
      selectedShift: this.selectedShift?.id,
      selectedEmployees: Object.keys(this.employeeCheckboxMap).filter(key => this.employeeCheckboxMap[key]),
      selectedTeams: Object.keys(this.teamCheckboxMap).filter(key => this.teamCheckboxMap[key]),
      ...advancedFields,
      ...this.deepWorkFieldsForPayload(),
      compareWith: this.selectedCompareWith,
    };

    // Push to the service bus first (works when running inside the global drawer)
    this._sharedService.reportFilterChanged$.next(payload);
    // Also emit via EventEmitter (works when used as a normal child component)
    this.filterChanged.emit(payload);
    this.closeDialog();
  }

  resetFilters() {
    this.tab = 'all';
    this.dateRange = 'last14daysExcludeToday';
    this.setDateAccordingOption(this.dateRange);
    this.selectedShift = null;
    this.selectedQuickPreset = null;
    this.selectedDaySelection = 'all';
    this.selectedTimeWindow = 'fullDay';
    this.selectedInterpretationMode = 'flexible';
    this.selectedBufferSetting = 'default';
    this.beforeBufferTime = 15;
    this.afterBufferTime = 15;
    this.postShiftAnalysisWindowHours = 0;
    this.postShiftAnalysisWindowMins = 0;
    this.customStartTime = '09:00';
    this.customEndTime = '18:00';
    this.selectedCustomDays = { 'Mon': true, 'Tue': true, 'Wed': true, 'Thu': true, 'Fri': true };
    this.deepWorkMinSessionMinutes = 90;
    this.deepWorkMinProductivityPct = 70;
    this.deepWorkMaxDistractionPct = 20;
    this.selectedCompareWith = 'previousPeriod';
    this.employeeCheckboxMap = {};
    this.teamCheckboxMap = {};

    const payload = {
      isReset: true,
      type: this.tab,
      dateRange: {
        start: this.selectedDateRange.value.start,
        end: this.selectedDateRange.value.end
      },
      selectedShift: this.selectedShift?.id,
      selectedEmployees: [],
      selectedTeams: [],
      daySelection: 'all',
      timeWindow: 'fullDay',
      interpretationMode: 'flexible',
      bufferSetting: 'default',
      customDays: null,
      startTime: null,
      endTime: null,
      beforeBufferTime: 0,
      afterBufferTime: 0,
      postShiftAnalysisWindowMinutes: 0,
      deepWorkMinSessionMinutes: 90,
      deepWorkMinProductivityPct: 70,
      deepWorkMaxDistractionPct: 20,
      compareWith: 'previousPeriod',
    };

    // Push to service bus (drawer mode) + EventEmitter (template mode)
    this._sharedService.reportFilterChanged$.next(payload);
    this.filterChanged.emit(payload);
    this.closeDialog();
  }

  openCalendarDropDown() {
    this.isOpenDateDropdown = !this.isOpenDateDropdown;
  }

  setDateAccordingOption(option: string) {
    const min = new Date(this.minDate);
    const max = new Date(this.maxDate);
    const { start, end } = this._timesheetPresetDateRange.getPresetRange(
      option as TimesheetDatePreset,
      max,
      min
    );
    this.selectedDateRange.patchValue({ start, end });
  }

  private enforceMaxRangeFromCurrentSelection(): void {
    if (this.isAdjustingDateRange) {
      return;
    }
    const startValue = this.selectedDateRange.value.start;
    const endValue = this.selectedDateRange.value.end;
    if (!startValue || !endValue) {
      return;
    }

    const start = moment(startValue).startOf('day');
    let end = moment(endValue).startOf('day');
    const maxAllowedEnd = start.clone().add(this.getMaxRangeDays() - 1, 'days');
    const hardMaxDate = moment(this.maxDate).startOf('day');

    let nextEnd = end;
    if (end.isBefore(start)) {
      nextEnd = start.clone();
    }
    if (nextEnd.isAfter(maxAllowedEnd)) {
      nextEnd = maxAllowedEnd.clone();
    }
    if (nextEnd.isAfter(hardMaxDate)) {
      nextEnd = hardMaxDate.clone();
    }

    if (!nextEnd.isSame(end, 'day')) {
      this.isAdjustingDateRange = true;
      this.selectedDateRange.patchValue({ end: nextEnd.toDate() }, { emitEvent: false });
      this.isAdjustingDateRange = false;
    }
  }

  get selectedFilterLabel(): string {
    if (this.selectedDateRange.value.start && this.selectedDateRange.value.end) {
      let startDate = moment(this.selectedDateRange.value.start).format("DD MMM YYYY");
      let endDate = moment(this.selectedDateRange.value.end).format("DD MMM YYYY");
      return `${startDate} - ${endDate}`;
    } else {
      return 'Select Range';
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    let dateRangeDropdownElement = this.dateRangeDropdown?.nativeElement as HTMLElement;
    let dateRangeButtonElement = this.dateRangeButton?.nativeElement as HTMLElement;

    // if clicks inside date range dropdown or on the button that opens it → DO NOTHING (allow user to interact with dropdown)
    const overlayClick =
      target.closest('.cdk-overlay-container');

    // If click is inside Angular Material overlay → DO NOTHING
    if (overlayClick) {
      return;
    }

    if (dateRangeButtonElement && dateRangeButtonElement.contains(target)) {
      return;
    }

    if (dateRangeDropdownElement && (dateRangeDropdownElement.contains(target) || target.closest('[data-date-range-btn]')) && this.isOpenDateDropdown) {
      return;
    } else {
      // If click is outside → close the dropdown
      if (this.isOpenDateDropdown) {
        this.isOpenDateDropdown = false;
      }
    }
  }

  getFormattedWorkingDays(days: number[]): string {
    if (!days || days.length === 0) return '';
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    if (days.length === 7) return 'All Days';

    // Check for Mon-Fri
    const isMonFri = days.length === 5 && [1, 2, 3, 4, 5].every(d => days.includes(d));
    if (isMonFri) return 'Mon–Fri';

    // Check for Sat-Sun
    const isWeekend = days.length === 2 && [6, 7].every(d => days.includes(d));
    if (isWeekend) return 'Sat–Sun';

    return days.sort().map(d => dayNames[d - 1]).join(', ');
  }

  getFormattedTimezone(timezone: string): string {
    if (!timezone) return '';
    try {
      const date = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'shortOffset'
      });
      const parts = formatter.formatToParts(date);
      const offset = parts.find(p => p.type === 'timeZoneName')?.value || '';

      const longFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'short'
      });
      const longParts = longFormatter.formatToParts(date);
      const abbr = longParts.find(p => p.type === 'timeZoneName')?.value || '';

      return `${offset} (${abbr})`;
    } catch (e) {
      return timezone;
    }
  }
}